import {
  Button,
  DataGrid,
  DateBox,
  Popup,
  ScrollView,
  TextBox,
  Validator,
} from "devextreme-react";
import usePopupState from "../hooks/usePopupState";
import { useEffect, useRef, useState } from "react";
import { Position, ToolbarItem } from "devextreme-react/popup";
import {
  Button as DataGridButton,
  Column,
  Editing,
  Item,
  Lookup,
  RowDragging,
  Scrolling,
  Toolbar,
  RequiredRule as DataGridRequiredRule,
  CustomRule as DataGridCustomRule,
} from "devextreme-react/data-grid";
import { getDateWithOffset, handleCellStyle, toastMsg } from "../utils";
import styled from "styled-components";
import { alert } from "devextreme/ui/dialog";
import { CustomRule, RequiredRule } from "devextreme-react/validator";
import validationEngine from "devextreme/ui/validation_engine";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
  .dx-validationgroup {
    width: 100%;
    padding: 0;
  }
  #totalTimeline {
    margin: 0 auto;
    margin-bottom: 32px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    width: 90%;
    .gridTitle {
      font-weight: bold;
      font-size: 16px;
      padding: 5px 11px;
      grid-area: 1/1/2 / span 4;
    }
    .gridHeader {
      padding: 8px 16px;
      text-align: center;
      font-weight: bold;
    }
    .gridData {
      .dx-texteditor-input {
        text-align: center;
        color: black;
      }
      #entireNote {
        .dx-texteditor-input {
          text-align: left;
        }
      }
      .dx-state-readonly {
        background-color: transparent !important;
      }
      .dx-texteditor.dx-editor-filled::after,
      .dx-state-focused::before {
        border-bottom: none;
      }
      .dx-texteditor.dx-editor-filled {
        background-color: transparent !important;
      }
      input:read-only {
        cursor: default;
      }
    }
    .gridHeader,
    .gridData {
      border-top: 1px solid #e0e0e0;
      border-bottom: 1px solid #e0e0e0;
      border-right: 1px solid #e0e0e0;
      &:nth-child(4n-2) {
        border-left: 1px solid #e0e0e0;
      }
    }
    .gridHeader {
      border-bottom: none;
      background-color: #e2efda;
    }
  }
  .dx-toolbar-item-content {
    font-weight: bold;
  }
  .dx-datebox-calendar .dx-dropdowneditor-icon::before {
    content: "\f026";
  }
  .dx-cell-modified:not(.dx-datagrid-invalid) {
    background-color: transparent !important;
  }
`;

// 필수값 검사가 걸려있어 행 추가/이동 후 다시 검증해야 하는 컬럼들.
const VALIDATED_FIELDS = ["dets", "startDate", "endDate"];

// DateBox/DataGrid가 Date 객체를 돌려주는 경우가 있어 서버로 보내기 전에
// 'yyyy-MM-dd'로 맞춘다. UTC로 바꾸면 하루씩 밀리므로 로컬 기준으로 만든다.
const toDateString = (value) => {
  if (!value) {
    return null;
  }

  if (!(value instanceof Date)) {
    return String(value).substring(0, 10);
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const TimelinePopup = ({ contrNo, isEditable, yearMonth }) => {
  const [itemDataSource, setItemDataSource] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const { mode, setPopupClose, setPopupEdit, setPopupOpen } = usePopupState();
  const detailedTlGridRef = useRef(null);
  const entireDurationRef = useRef(null);
  const entireEndDateRef = useRef(null);
  const entireNoteRef = useRef(null);
  const entireStartDateRef = useRef(null);
  const tempSeqRef = useRef(0);

  useEffect(() => {
    setTimeline(
      new CustomStore({
        load: async (loadOptions) => {
          const { contrNo } = loadOptions.userData;
          console.log(contrNo);
          //! api GET R10. Get the timeline
          const response = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/tl/${contrNo}`
          );
          return response.data;
        },
        update: async (contrNo, { entireTimeline, detailedTimeline }) => {
          //! api PUT U3. Update the timeline
          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/tl/${contrNo}`,
            {
              entireTimeline,
              detailedTimeline,
            },
            { params: { yearMonth } }
          );
          toastMsg(`Timeline이 정상적으로 수정되었습니다`, true);
        },
        errorHandler: (error) => {
          toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
          console.log(error);
        },
      })
    );

    //! api GET R11. Get the details of detailed timeline
    axios
      .get(
        `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/tl/dets`
      )
      .then((res) => setItemDataSource(res.data.dets));
  }, [yearMonth]);

  useEffect(() => {
    if (mode === "view") {
      timeline.load({ userData: { contrNo } }).then((res) => {
        const { entireTimeline, detailedTimeline } = res;
        detailedTlGridRef.current.instance.option(
          "dataSource",
          detailedTimeline
        );
        entireStartDateRef.current.instance.option(
          "value",
          entireTimeline.startDate
        );
        entireEndDateRef.current.instance.option(
          "value",
          entireTimeline.endDate
        );
        entireNoteRef.current.instance.option("value", entireTimeline.note);
      });
    }
  }, [mode]);

  const calculateDuration = (startDate, endDate) => {
    const oneDayInMilliseconds = 1000 * 60 * 60 * 24;

    const start = new Date(startDate);
    const end = new Date(endDate);

    const differenceInMilliseconds = end - start;
    const differenceInDays = differenceInMilliseconds / oneDayInMilliseconds;

    const durationInYears = differenceInDays / 365;
    const roundedDuration = Math.round(durationInYears * 1000) / 1000;
    const roundedResult = roundedDuration.toFixed(2); // 소수점 둘째자리까지 표시

    return parseFloat(roundedResult); // 결과를 숫자로 반환
  };

  const handleAddRow = (ref) => {
    //* row 추가 버튼을 누르면 dataSource에 추가되고, 각 셀에 대한 validation을 진행한다.
    const newData = {
      // keyExpr가 SEQ라서 key 없이 추가하면 새 row끼리 key(undefined)가 겹쳐
      // 편집/삭제가 엉뚱한 row에 적용된다. 저장 시 서버가 SEQ를 1부터 다시
      // 매기므로, 기존 SEQ와 겹칠 수 없는 음수를 임시 key로 부여한다.
      SEQ: --tempSeqRef.current,
      startDate: null,
      endDate: null,
    };

    const component = ref.current.instance;
    component
      .getDataSource()
      .store()
      .push([{ type: "insert", data: newData, index: 0 }]);

    setTimeout(() => {
      // row의 validation.
      for (let dataField of VALIDATED_FIELDS) {
        const value = component.cellValue(0, dataField);
        component.cellValue(0, dataField, value);
      }
    }, 800);
  };

  const handleClickSave = async () => {
    const isGroupValid = validationEngine.validateGroup().isValid;
    const isGridValid = validateGridValue(detailedTlGridRef);

    if (!isGroupValid || !isGridValid) {
      return;
    }

    const entireTimeline = {
      startDate: toDateString(
        entireStartDateRef.current.instance.option("value")
      ),
      endDate: toDateString(entireEndDateRef.current.instance.option("value")),
      note: entireNoteRef.current.instance.option("value"),
    };

    // batch 모드의 변경분을 먼저 store에 반영하고, store에서 전체 row를 읽는다.
    // getVisibleRows()는 virtual scrolling 때문에 로드된 row만 돌려주는데
    // 서버는 받은 row로 전체를 갈아끼우므로, 스크롤하지 않은 row가 지워진다.
    const gridInstance = detailedTlGridRef.current.instance;
    await gridInstance.saveEditData();

    if (gridInstance.hasEditData()) {
      // DataGrid 자체 validation에 걸린 경우. 해당 셀이 grid에 표시된다.
      return;
    }

    const rows = await gridInstance.getDataSource().store().load();
    // SEQ는 서버가 순서대로 다시 매기므로 임시 key를 보내지 않는다.
    const detailedTimeline = rows.map(({ SEQ, ...row }) => ({
      ...row,
      startDate: toDateString(row.startDate),
      endDate: toDateString(row.endDate),
    }));

    timeline
      .update(contrNo, { entireTimeline, detailedTimeline })
      .then(() => setPopupOpen())
      // 저장에 실패하면 edit 모드를 유지해 입력값이 사라지지 않게 한다.
      // (에러 토스트는 CustomStore의 errorHandler가 띄운다.)
      .catch(() => {});
  };

  const handleCustomItemCreate = (e) => {
    if (!e.text) {
      e.customItem = null;
      return;
    }
    const newItem = e.text;
    const dataSource = e.component.getDataSource();
    const isExistingItem = e.component
      .getDataSource()
      .items()
      .includes(newItem);
    if (isExistingItem) {
      // 이미 존재하는 데이터라면 customItem을 newItem으로 지정한다.
      e.customItem = newItem;
    } else {
      // 존재하지 않는 데이터라면 dataSource에 추가하고 customItem을 newItem으로 지정한다.
      e.customItem = dataSource
        .store()
        .insert(newItem)
        .then(() => dataSource.load())
        .then(() => newItem)
        .catch((error) => {
          throw error;
        });
    }
  };

  const handleDeleteRow = (e) => {
    // batch 모드의 deleteRow()는 삭제 표시만 하고 실제로 빼지 않아
    // 저장할 때 다시 들어간다. 항상 dataSource에서 remove 한다.
    e.component
      .getDataSource()
      .store()
      .push([{ type: "remove", key: e.row.data.SEQ }]);
  };

  const handleDurationValue = (e) => {
    const formattedStartDate = e.startDate;
    const formattedEndDate = e.endDate;
    return `${formattedStartDate} ~ ${formattedEndDate}`;
  };

  const handleEntireDate = (e) => {
    // Datebox의 value를 수정하고, 기간 textbox의 value를 수정.
    const isStartDate = e.element.id === "entireStartDate";
    const startDate = entireStartDateRef.current.instance.option("value");
    const endDate = entireEndDateRef.current.instance.option("value");

    // Datebox의 value 수정하고, 기간 textbox의 value 수정
    e.component.option("value", e.value);
    const duration = calculateDuration(
      isStartDate ? e.value : startDate,
      isStartDate ? endDate : e.value
    );
    entireDurationRef.current.instance.option(
      "value",
      `${duration} ${duration > 1 ? "years" : "year"}`
    );

    // 시작일과 종료일의 validation 실행.
    validationEngine.validateGroup();
  };

  const handleOnReorder = (e) => {
    const newData = e.itemData;
    // deleteRow()는 batch 모드에서 실제로 빼지 않아 row가 복제된다.
    // 옮긴 row를 dataSource에서 remove 하고 새 위치에 insert 한다.
    e.component
      .getDataSource()
      .store()
      .push([{ type: "remove", key: newData.SEQ }]);
    e.component
      .getDataSource()
      .store()
      .push([{ type: "insert", data: newData, index: e.toIndex }]);

    setTimeout(() => {
      // 옮겨진 row의 validation.
      for (let dataField of VALIDATED_FIELDS) {
        const value = e.component.cellValue(e.toIndex, dataField);
        e.component.cellValue(e.toIndex, dataField, value);
      }
    }, 800);
  };

  const validateDataGridDateAfterEntire = (e) => {
    const entireStartDate = getDateWithOffset(
      entireStartDateRef.current.instance.option("value")
    );
    const entireEndDate = getDateWithOffset(
      entireEndDateRef.current.instance.option("value")
    );
    const valueDate = new Date(e.value);

    // valueDate가 entireStartDate와 entireEndDate 사이에 있는지 검증
    return valueDate >= entireStartDate && valueDate <= entireEndDate;
  };

  const validateDataGridDateValue = (e) => {
    const startDate = e.data.startDate
      ? getDateWithOffset(e.data.startDate)
      : null;
    const endDate = e.data.endDate ? getDateWithOffset(e.data.endDate) : null;
    const valueDate = new Date(e.value);

    if (e.column.dataField === "startDate" && endDate && valueDate > endDate) {
      return false;
    }

    if (
      e.column.dataField === "endDate" &&
      startDate &&
      valueDate < startDate
    ) {
      return false;
    }

    return true;
  };

  const validateDateBoxValue = (e) => {
    // timeline은 CustomStore 인스턴스라 entireTimeline을 갖고 있지 않다.
    // 지금 입력된 값끼리 비교해야 한다.
    const startDate = entireStartDateRef.current.instance.option("value");
    const endDate = entireEndDateRef.current.instance.option("value");
    const valueDate = new Date(e.value);
    const validation = e.rule.name;
    if (validation === "SDValidation" && endDate) {
      return valueDate <= new Date(endDate);
    }
    if (validation === "EDValidation" && startDate) {
      return valueDate >= new Date(startDate);
    }
    return true;
  };

  const validateGridValue = (ref) => {
    const gridInstance = ref.current.instance;

    const validationState =
      gridInstance.getController("validating")._validationState;
    const errorMessages = [];

    // 모든 cell의 validation.
    const visibleRowIdx = gridInstance
      .getVisibleRows()
      .map((row) => row.rowIndex);
    visibleRowIdx.forEach((rowIdx) => {
      ["dets", "startDate", "endDate"].forEach((dataField) => {
        const value = gridInstance.cellValue(rowIdx, dataField);
        gridInstance.cellValue(rowIdx, dataField, value);
      });
    });

    const visibleRowKeys = gridInstance.getVisibleRows().map((row) => row.key);
    validationState.forEach((row) => {
      if (visibleRowKeys.includes(row.key) && row.validationResults) {
        const cellValidation = Object.values(row.validationResults);
        cellValidation.forEach((c) => {
          if (c.status === "invalid") {
            const rule = c.brokenRule;
            const message =
              rule.type === "required"
                ? `${c.name}은 필수 항목입니다`
                : rule.message;
            errorMessages.push(message);
          }
        });
      }
    });

    if (errorMessages.length > 0) {
      alert(errorMessages.join("<br />"), "Validation Error");
      return false;
    }
    return true;
  };

  return (
    <>
      <Button
        onClick={setPopupOpen}
        stylingMode="contained"
        text="TIMELINE"
        type="success"
      />
      {mode !== "close" && (
        <Popup
          height={"auto"}
          hideOnOutsideClick={true}
          maxHeight={"90vh"}
          maxWidth={"90vw"}
          onHiding={setPopupClose}
          showCloseButton={true}
          title="Timeline"
          visible={true}
          width={1200}
        >
          <ToolbarItem
            location="after"
            options={{
              disabled: !isEditable,
              icon: "edit",
              onClick: setPopupEdit,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={mode !== "edit"}
            widget="dxButton"
          />
          <ToolbarItem
            location="after"
            options={{
              icon: "save",
              onClick: handleClickSave,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={mode === "edit"}
            widget="dxButton"
          />
          <ToolbarItem
            location="after"
            options={{
              icon: "revert",
              onClick: setPopupOpen,
              stylingMode: "contained",
              type: "normal",
            }}
            visible={mode === "edit"}
            widget="dxButton"
          />
          <Position at="center" collision="fit" my="center" />
          <ScrollView width="100%" height="100%">
            <TimelineWrapper>
              <div id="totalTimeline">
                <div className="gridTitle">Entire Timeline</div>
                <div className="gridHeader">Start Date</div>
                <div className="gridHeader">End Date</div>
                <div className="gridHeader">Duration</div>
                <div className="gridHeader">Note</div>
                <div className="gridData">
                  <DateBox
                    displayFormat="yyyy-MM-dd"
                    id="entireStartDate"
                    invalidDateMessage="YYYY-MM-DD 형식이어야 합니다"
                    onValueChanged={handleEntireDate}
                    readOnly={mode !== "edit"}
                    ref={entireStartDateRef}
                    useMaskBehavior={true}
                  >
                    <Validator>
                      <RequiredRule message="" />
                      <CustomRule
                        ignoreEmptyValue={true}
                        message={"종료일보다 늦을 수 없습니다"}
                        name="SDValidation"
                        reevaluate={true}
                        validationCallback={validateDateBoxValue}
                      />
                    </Validator>
                  </DateBox>
                </div>
                <div className="gridData">
                  <DateBox
                    displayFormat="yyyy-MM-dd"
                    id="entireEndDate"
                    invalidDateMessage="YYYY-MM-DD 형식이어야 합니다"
                    onValueChanged={handleEntireDate}
                    readOnly={mode !== "edit"}
                    ref={entireEndDateRef}
                    useMaskBehavior={true}
                  >
                    <Validator>
                      <RequiredRule message="" />
                      <CustomRule
                        ignoreEmptyValue={true}
                        message={"시작일보다 빠를 수 없습니다"}
                        name="EDValidation"
                        reevaluate={true}
                        validationCallback={validateDateBoxValue}
                      />
                    </Validator>
                  </DateBox>
                </div>
                <div className="gridData">
                  <TextBox readOnly={true} ref={entireDurationRef} />
                </div>
                <div className="gridData">
                  <TextBox
                    id="entireNote"
                    readOnly={mode !== "edit"}
                    ref={entireNoteRef}
                  />
                </div>
              </div>
              <DataGrid
                allowColumnResizing={true}
                columnResizingMode={"nextColumn"}
                height={"100%"}
                id="detailedTimelineGrid"
                keyExpr="SEQ"
                onCellPrepared={handleCellStyle}
                ref={detailedTlGridRef}
                showBorders={true}
                showColumnLines={true}
                width={"90%"}
                wordWrapEnabled={true}
              >
                <Editing
                  allowAdding={mode === "edit"}
                  allowDeleting={mode === "edit"}
                  allowUpdating={mode === "edit"}
                  mode="batch"
                />
                <Scrolling mode="virtual" showScrollbar={"always"} />
                <RowDragging
                  allowReordering={mode === "edit"}
                  onReorder={handleOnReorder}
                />
                <Toolbar>
                  <Item
                    location="before"
                    name="title"
                    text="Detailed Timeline"
                  />
                  <Item
                    location="after"
                    name="addRowBtn"
                    options={{
                      icon: "add",
                      onClick: () => handleAddRow(detailedTlGridRef),
                      visible: mode === "edit",
                    }}
                    widget="dxButton"
                  />
                </Toolbar>
                <Column
                  dataField="dets"
                  alignment="center"
                  caption="Detailed Timeline"
                  editorOptions={{
                    acceptCustomValue: true,
                    // dets 컬럼이 nvarchar(100)이라 넘으면 저장 시 에러가 난다.
                    maxLength: 100,
                    onCustomItemCreating: handleCustomItemCreate,
                    searchEnabled: true,
                    wrapItemText: true,
                  }}
                  showEditorAlways={mode === "edit"}
                  width={230}
                >
                  <Lookup dataSource={itemDataSource} />
                  <DataGridRequiredRule message="" />
                </Column>
                <Column
                  dataField="duration"
                  alignment="center"
                  calculateCellValue={handleDurationValue}
                  caption="Duration"
                  visible={mode !== "edit"}
                  width={460}
                />
                <Column
                  dataField="startDate"
                  alignment="center"
                  caption="Start Date"
                  dataType="date"
                  editorOptions={{ useMaskBehavior: true }}
                  format="yyyy-MM-dd"
                  showEditorAlways={mode === "edit"}
                  visible={mode === "edit"}
                  width={200}
                >
                  <DataGridCustomRule
                    ignoreEmptyValue={true}
                    message={"종료일보다 늦을 수 없습니다"}
                    reevaluate={true}
                    validationCallback={validateDataGridDateValue}
                  />
                  <DataGridCustomRule
                    ignoreEmptyValue={true}
                    message={"시작일이 전체 타임라인 사이에 위치해야 합니다"}
                    reevaluate={true}
                    validationCallback={validateDataGridDateAfterEntire}
                  />
                </Column>
                <Column
                  dataField="endDate"
                  alignment="center"
                  caption="End Date"
                  dataType="date"
                  editorOptions={{ useMaskBehavior: true }}
                  format="yyyy-MM-dd"
                  showEditorAlways={mode === "edit"}
                  visible={mode === "edit"}
                  width={200}
                >
                  <DataGridRequiredRule message="" />
                  <DataGridCustomRule
                    ignoreEmptyValue={true}
                    message={"시작일보다 앞설 수 없습니다"}
                    reevaluate={true}
                    validationCallback={validateDataGridDateValue}
                  />
                  <DataGridCustomRule
                    ignoreEmptyValue={true}
                    message={"종료일이 전체 타임라인 사이에 위치해야 합니다"}
                    reevaluate={true}
                    validationCallback={validateDataGridDateAfterEntire}
                  />
                </Column>
                <Column
                  dataField="note"
                  alignment="left"
                  caption="Note"
                  showEditorAlways={mode === "edit"}
                />
                <Column visible={mode === "edit"} type="buttons" width={50}>
                  <DataGridButton icon="trash" onClick={handleDeleteRow} />
                </Column>
              </DataGrid>
            </TimelineWrapper>
          </ScrollView>
        </Popup>
      )}
    </>
  );
};

export default TimelinePopup;
